// Session = "who the dashboard is acting as".
//
//   - vite dev  → the seeded `dev_pat_` bearer by default (override via
//                 PUBLIC_DEV_API_TOKEN), OR the real cookie session when
//                 PUBLIC_DEV_API_TOKEN=cookie — see below.
//   - prod      → COOKIE_SESSION: login mints an httpOnly `dz_session` cookie the
//                 API resolves. JS never sees the cookie, so we use a non-null
//                 marker to keep the app's `enabled` / `hasSession` gates "on"
//                 and let `apiFetch` fall back to `credentials:'include'`. A 401
//                 means "not signed in" → the app renders <Login/>.
//   - tests     → null (neither DEV nor PROD), i.e. the pre-SSO disabled state.
//
// `apiFetch` attaches a bearer ONLY for a real `dev_pat_` token, so the marker
// never leaks into an Authorization header.
//
// ## Why `PUBLIC_DEV_API_TOKEN=cookie` exists (#756)
//
// A PAT and a cookie session are NOT interchangeable identities. A PAT resolves
// to an account with a fixed scope set and no human behind it, so `/v1/me`
// returns `user: null`, `memberships: []`, `role: null`, `tos: null` — which
// means the org switcher, the role pills, the ToS consent wall, the closed-beta
// waitlist wall and the members roster all render their fallbacks and can never
// be exercised in `vite dev`. Pointing this at `cookie` puts the dev SPA on the
// same cookie path prod uses, so `/auth/dev/login?as=user|admin` (the local
// persona login) actually drives the app. Vite already proxies `/v1` and
// `/auth` to the API, so the cookie is same-origin and no CORS work is needed.

import type { RegionCode } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';

/** Fixed local-dev bearer — mirrors `DEV_API_TOKEN` in `packages/db/src/seeds/dev.ts`. */
const DEV_TOKEN_FALLBACK = 'dev_pat_localdev0000000000000000';

/** Marker credential: "authenticate via the httpOnly session cookie" (no bearer). */
export const COOKIE_SESSION = '__cookie_session__' as const;

/**
 * Opt `vite dev` onto the cookie path: `PUBLIC_DEV_API_TOKEN=cookie`. Spelled as
 * a plain word rather than the internal marker so the env value stays something
 * a human would guess, and so the marker itself is never a valid env input.
 */
const DEV_COOKIE_OPT_IN = 'cookie' as const;

/** Resolve the DEV credential: the cookie opt-in, an explicit PAT, or the seeded default. */
function devSessionToken(): string {
  const configured = import.meta.env['PUBLIC_DEV_API_TOKEN'];
  if (configured === DEV_COOKIE_OPT_IN) return COOKIE_SESSION;
  return configured ?? DEV_TOKEN_FALLBACK;
}

/** The dashboard's session credential. See file header. */
export const SESSION_TOKEN: string | null = import.meta.env.DEV
  ? devSessionToken()
  : import.meta.env.PROD
    ? COOKIE_SESSION
    : null;

/** The human behind a user-bound session; `null` for a PAT / machine credential. */
export type MeUser = {
  id: string;
  github_user_id: number;
  login: string;
  email: string | null;
  avatar: string | null;
  // The region the person declared (#5881), ISO 3166-1 alpha-2 — typed as the
  // domain's `RegionCode` literal union, not a free `string`, so a comparison
  // like `user?.region === 'CN'` is checked at compile time and the property
  // name on `ISO_3166_1_ALPHA2` is the same set the API can publish. `null`
  // means "never asked", which is what raises the one-time region question;
  // optional so an older `/v1/me` (PAT, or pre-#5881 cookie) still parses, and
  // absent never asks.
  region?: RegionCode | null;
};

/** One account the signed-in user can act as — a row in the org switcher. */
export type MembershipView = {
  account_id: string;
  github_login: string | null;
  kind: string;
  role: string;
  /**
   * Whether THIS session may open the org (#466 § 4): an SSO-minted session is
   * pinned to the orgs whose IdP signed it in. Read through `isReachable`
   * (`lib/memberships.ts`), which treats only an explicit `false` as closed.
   */
  reachable: boolean;
  /**
   * Where to sign in when not reachable: a full-page leg (GitHub, or the org's
   * SSO) that accepts `?return_to=` and adds the org to this same session.
   */
  signIn: { kind: 'github' | 'sso'; url: string } | null;
  /** The org's enabled IdP, and whether this user already holds that identity. */
  sso: { slug: string; linked: boolean } | null;
};

/** `GET /v1/me` response (see `apps/api/src/routes/me.ts`). */
export type Me = {
  id: string;
  kind: string;
  github_login: string;
  email: string | null;
  tier: string;
  onboarding_state: string;
  oss_verified: boolean;
  created_at: string;
  // Identity + org switcher (slice 02). The API always sends these; they're
  // optional here so a partial/legacy `/v1/me` (a PAT or the pre-identity cookie)
  // parses too — `user`/`role` are then null and `memberships` empty.
  active_account_id?: string;
  role?: string | null;
  user?: MeUser | null;
  memberships?: readonly MembershipView[];
  // The credential's OWN scope list in the active account — what `GET /v1/me`
  // publishes (`scopes: [...scopes]`, `apps/api/src/routes/me.ts`), the same
  // list the MCP `whoami` verb answers. It is the AUTHORITY on what this
  // credential may do, published ahead of the refusal: `role` does not answer
  // it (a PAT carries none, a cap narrows a role's ceiling), so a client
  // NEVER re-derives it from the role table — it reads this list.
  // Optional so a partial/legacy `/v1/me` still parses, and an ABSENT list
  // keeps every navigation row (fail open — the server refuses what it must).
  scopes?: readonly string[];
  // Platform-staff flag (admin radar). A `developerz-ai` org staff member sees
  // the admin.developerz.ai views; everyone else hits the closed-beta wall.
  // Optional so a partial/legacy `/v1/me` still parses (then falsy → not staff).
  is_platform_admin?: boolean;
  // ToS/consent (#554). Present for a user-bound session; null for a PAT / legacy
  // cookie. `needsConsent()` blocks the app on the consent screen while
  // `accepted_version !== current_version` (also re-prompts on a version bump).
  tos?: {
    current_version: string;
    accepted_version: string | null;
    accepted_at: string | null;
  } | null;
  // Closed-beta approval — the OUTERMOST rung of the server's ladder. `false` is
  // WAITLISTED: `sessionScopes` (`apps/api/src/middleware/auth.ts`) has already
  // capped this session to `read:account` alone, whatever its role. `null` is
  // the rung not applying (a PAT / machine credential — no human behind it).
  // Optional so a partial/legacy `/v1/me` still parses; absent reads as `null`.
  approved?: boolean | null;
  // The separate cross-border consent (#5881). `missing` is the SERVER's verdict:
  // true exactly when `sessionScopes` has capped this session for it (a `CN`
  // region without a current, unwithdrawn grant). `current_version` is the notice
  // version a grant echoes back. Null for a PAT; optional so an older `/v1/me`
  // still parses, and absent never walls.
  cross_border_consent?: { current_version: string; missing: boolean } | null;
  // This session is pinned to the SSO org it signed in through, so the personal
  // GitHub-token surface (`/v1/me/github-token`) refuses it — the server publishes
  // that route's own gate (#7426). Optional: absent reads as "not reported", and the
  // view then asks and renders the 403 as an answer.
  sso_pinned?: boolean;
};

/**
 * The two account kinds `/v1/me` reports — a PERSONAL GitHub account or an
 * organisation. Billing reads it: Solo is a personal-accounts-only plan, so the
 * seat cart offers it on `'user'` and withholds it on `'org'`.
 *
 * {@link Me.kind} stays `string` on purpose and this is a separate type rather
 * than a narrowing of it: `/v1/me` is read through `apiFetch<Me>` with no
 * schema, an unchecked cast, so declaring the union on the wire type would be a
 * guarantee nothing validates — precisely the comment-asserting-a-guarantee
 * this type exists to avoid. {@link accountKind} is the one seam that turns the
 * wire's `string` into one of these.
 */
export type AccountKind = 'user' | 'org';

/**
 * The account's kind as an {@link AccountKind}, or `undefined` for a kind this
 * client does not know — an unread `me` included.
 *
 * `undefined` means NOT KNOWN, never a kind in its own right, and every caller
 * that gates a control on it must fail OPEN: a server that starts sending a
 * third kind would otherwise silently reclassify those accounts as whichever
 * branch the gate's `else` happens to be. The one caller today is the seat cart
 * (`components/SeatCheckout.tsx`), which keeps Solo on the menu for anything
 * but an explicit `'org'` and lets the route's 409 arbitrate.
 */
export const accountKind = (kind: string | undefined): AccountKind | undefined =>
  kind === 'user' || kind === 'org' ? kind : undefined;

/** True when the signed-in user must (re)accept the current ToS before any access (#554). */
export function needsConsent(me: Me): boolean {
  return !!me.tos && me.tos.accepted_version !== me.tos.current_version;
}

/** True when the signed-in person has never been asked their region (#5881). */
export function needsRegion(me: Me): boolean {
  return me.user?.region === null;
}

/**
 * True when the server has capped this session for the missing cross-border
 * consent (#5881). Explicit `true` only: absence is never a wall.
 */
export function needsCrossBorderConsent(me: Me): boolean {
  return me.cross_border_consent?.missing === true;
}

/**
 * True only for a session the server has WAITLISTED — an EXPLICIT `false`.
 *
 * Absent and `null` never wall, and that direction is deliberate: the server is
 * the real gate (it has already capped the session to `read:account`), so this
 * only picks a screen. Failing CLOSED here would render "we were not told" as
 * "suspended" — a suspension nobody applied, on a credential that may have no
 * human to suspend.
 */
export function isWaitlisted(me: Me): boolean {
  return me.approved === false;
}

/**
 * The signed-in account, or a disabled query when there's no session (`token`
 * is `null` — tests / pre-SSO). `token` is a parameter — defaulting to
 * `SESSION_TOKEN` — so it's testable without Vite env and overridable.
 *
 * Keyed on the active account (#916). `/v1/me` is NOT tenant-invariant: `role`
 * is the caller's role IN the active account and `apiFetch` scopes the request
 * with `X-Dz-Account` off the URL. Keying on the token alone meant switching
 * org re-fetched every OTHER query (they all carry `activeAccountId`) while
 * `me` was served from cache under the old tenant — so the thirteen views that
 * read it (the rail's user block, the switcher, the members roster's actor
 * role, the admin gate) kept rendering the previous org until a hard reload.
 *
 * NOT the gate's probe: `<App/>` gates on {@link useSessionMe}, keyed on the
 * credential alone, so the gate never depends on the key the Router's
 * `<OrgScope>` transitively sets. `placeholderData` keeps the previous org's
 * `me` on screen (role pills, switcher) while the next org's read is in flight.
 */
export function useMe(token: string | null = SESSION_TOKEN) {
  return createQuery<Me>(() => ({
    queryKey: ['me', token, activeAccountId()],
    enabled: token !== null,
    placeholderData: (prev: Me | undefined) => prev,
    queryFn: () => apiFetch<Me>('/v1/me', token),
  }));
}

/**
 * The SESSION's `/v1/me` — asked with no org claim (`scope: 'session'`) and keyed
 * on the credential alone. It answers "who is signed in, and are they walled"
 * (`approved`, `tos`), which no URL org changes, plus the session's own active
 * account (the legacy catch-all's landing, `LegacyRedirect`).
 *
 * `<App/>`'s gate reads THIS, never {@link useMe}. Gating on the org-keyed read
 * made a 403 on `/org/<not-mine>` (typo, stale bookmark, removed membership) or a
 * 5xx during an org switch look like "no identity": the gate answered `outage`
 * and unmounted the Router, leaving no way out but a reload. The org's refusal
 * belongs to the org (`<OrgScope/>` reports it inside the shell).
 *
 * A write that changes the session's walls re-reads THIS query: the consent
 * accept (`App`) and the invitation redeem (`routes/Invite.tsx`).
 */
export function useSessionMe(token: string | null = SESSION_TOKEN) {
  return createQuery<Me>(() => ({
    // Its own first segment, never `['me', token, <x>]`: the org-scoped key's
    // third slot is an org login, and `session` is a login someone can own.
    queryKey: ['session_me', token],
    enabled: token !== null,
    queryFn: () => apiFetch<Me>('/v1/me', token, undefined, { scope: 'session' }),
  }));
}

/**
 * The waitlist wall's approval probe (`lib/approval-poll.ts`): a raw `/v1/me` read
 * with the SESSION's claim, OUTSIDE the `session_me` cache — the wall moves the URL
 * before the gate's own query learns, so the app mounts on the funnel.
 */
export async function probeApproval(token: string | null): Promise<boolean> {
  const me = await apiFetch<Me>('/v1/me', token, undefined, { scope: 'session' });
  return !isWaitlisted(me);
}
